import { memo, useCallback } from 'react';

import { FolderPlusIcon } from 'lucide-react';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { Presence } from '@/shared/types/presence';
import { RootState } from '@/shared/types/state';

import { Collections } from './SubNavIntent';
import TreeView from './TreeView/TreeView';

import styles from './SubNav.module.scss';

export type SubnavType = 'dialogs' | 'intents' | 'entities';

interface CollectionProps {
  collection: string;
  new_collection: string;
}

export interface SubNavProps {
  title?: string;
  skeleton?: boolean;
  onCreateClick: () => void;
  onCollectionUpdate: ({ id, collection }: { id: string; collection: string }) => void;
  onCreateCollection?: ({ id }: { id: string }) => void;
  onCollectionSubmit: (data: CollectionProps) => void;
  menuItems?: { label: string; value: string }[];
  createMenuClick?: (value: string) => void;
  navTitle?: string;
  collections: Collections;
  lastCreatedCollection?: string;
  whoIsViewing?: Presence[];
  subnavType?: SubnavType;
  isCreating?: boolean;
}

const SubNav = ({
  title,
  skeleton,
  onCreateClick,
  onCollectionUpdate,
  onCreateCollection,
  onCollectionSubmit,
  menuItems,
  createMenuClick,
  navTitle,
  collections,
  lastCreatedCollection,
  whoIsViewing = [],
  subnavType,
  isCreating,
}: SubNavProps) => {
  const { t } = useTranslation();
  const [{ isOver }, drop] = useDrop({
    accept: 'treeItem',
    drop: onCreateCollection,
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });
  const lowerCasedTitle = title?.toLowerCase();
  const isEmpty = Object.keys(collections).length === 1 && !collections._orphans.length;

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const handleSimpleCreateClick = useCallback(() => {
    if (!menuItems) {
      onCreateClick();
    }
  }, [menuItems, onCreateClick]);

  const handleCreateMenuClick = useCallback(
    (value: string) => {
      createMenuClick(value);
    },
    [createMenuClick]
  );

  const createButton = (
    <Button
      size="sm"
      className="w-full"
      data-testid="create-item"
      onClick={handleSimpleCreateClick}
      disabled={!canWrite}
      isLoading={isCreating}
    >
      {t('subnav.create', { 0: lowerCasedTitle })}
    </Button>
  );

  return (
    <div className={styles.subnav}>
      <div className={styles.actions}>
        {menuItems ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{createButton}</DropdownMenuTrigger>
            <DropdownMenuContent align="center" sideOffset={4} className="min-w-fit">
              {menuItems.map(({ label, value }) => (
                <DropdownMenuItem key={value} onSelect={() => handleCreateMenuClick(value)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          createButton
        )}
      </div>

      <div className={styles.content}>
        {subnavType === 'dialogs' && (
          <div className="mx-3">
            <ContextualHelp title={t('dialog.contextual_help.title')} name="about_dialogs">
              {t('dialog.contextual_help.message')}
            </ContextualHelp>
          </div>
        )}

        {subnavType === 'entities' && (
          <div className="mx-3">
            <ContextualHelp title={t('entity.contextual_help.title')} name="about_entities">
              {t('entity.contextual_help.message')}
            </ContextualHelp>
          </div>
        )}

        <TreeView
          skeleton={skeleton}
          onCollectionUpdate={onCollectionUpdate}
          onCollectionSubmit={onCollectionSubmit}
          collections={collections}
          lastCreatedCollection={lastCreatedCollection}
          whoIsViewing={whoIsViewing}
          navTitle={navTitle}
          subnavType={subnavType}
        />
      </div>
      {!isEmpty && (
        <div
          className={cn(styles.dropBox)}
          ref={(node) => {
            drop(node);
          }}
        >
          <div className={cn(styles.dropInner, { [styles.dragging]: isOver })}>
            <FolderPlusIcon className="text-muted-foreground" />
            <span className="text-sm leading-5 font-normal">{t('subnav.drop')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(SubNav);
